/* eslint-disable react/no-array-index-key */
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import {
    Box, Typography, TextField, Select, MenuItem, Autocomplete,
    Table, TableBody, TableRow, TableCell, TableHead,
    IconButton, Button, InputAdornment, Tooltip, alpha,
} from '@mui/material';
import Iconify from 'src/components/Iconify';

const PAYMENT_OPTIONS = [
    { value: 'no',       label: 'No payment plan' },
    { value: 'monthly',  label: 'Monthly instalments' },
    { value: 'external', label: 'Finance arranged externally' },
];

function formatCurrency(val) {
    const n = parseFloat(val);
    if (Number.isNaN(n)) return '';
    return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function TreatmentPlanCostTable({
    costItems,
    deposit,
    paymentPlan,
    included,
    excluded,
    onChange,
    readOnly,
    treatments,
}) {
    // ── Treatment options for autocomplete ────────────────────────────────────
    const treatmentOptions = (treatments || []).map((t) => ({
        label: t?.treatment_item?.name || '',
        price: t?.custom_price || t?.price || '',
        colour: t?.colour || '',
        category: t?.treatment_item?.treatment_category?.name || '',
    })).filter((o) => o.label);

    // ── Item helpers ──────────────────────────────────────────────────────────
    const updateItem = (id, field, value) => {
        const updated = costItems.map((item) => {
            if (item.id !== id) return item;
            const next = { ...item, [field]: value };
            const qty  = parseFloat(next.qty) || 0;
            const unit = parseFloat(next.unitPrice) || 0;
            next.lineTotal = (qty * unit).toFixed(2);
            return next;
        });
        onChange('costItems', updated);
    };

    const handleTreatmentSelect = (itemId, newValue) => {
        // newValue can be a string (free text) or an option object (selected from dropdown)
        const isOption = typeof newValue === 'object' && newValue !== null;
        const description = isOption ? newValue.label : (newValue || '');
        const updated = costItems.map((item) => {
            if (item.id !== itemId) return item;
            const next = { ...item, description };
            // Auto-fill price when a treatment is selected from dropdown
            if (isOption && newValue.price) {
                next.unitPrice = String(newValue.price);
                next.treatmentName = newValue.label;
                next.colour = newValue.colour || '';
            }
            const qty  = parseFloat(next.qty) || 0;
            const unit = parseFloat(next.unitPrice) || 0;
            next.lineTotal = (qty * unit).toFixed(2);
            return next;
        });
        onChange('costItems', updated);
    };

    const addItem = () => {
        const newItem = { id: uuidv4(), description: '', qty: '1', unitPrice: '', lineTotal: '' };
        onChange('costItems', [...costItems, newItem]);
    };

    const removeItem = (id) => {
        onChange('costItems', costItems.filter((i) => i.id !== id));
    };

    const grandTotal = costItems.reduce((sum, item) => {
        const qty  = parseFloat(item.qty) || 0;
        const unit = parseFloat(item.unitPrice) || 0;
        return sum + qty * unit;
    }, 0);

    const showPaymentDetails = paymentPlan?.type && paymentPlan.type !== 'no';

    // ── Cell style ────────────────────────────────────────────────────────────
    const cellSx = { py: '6px', px: '8px', border: 'none' };
    const inputSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontSize: '13px',
            bgcolor: readOnly ? '#F9FAFB' : '#fff',
            '& fieldset': { borderColor: 'rgba(145,158,171,0.2)' },
            '&:hover fieldset': { borderColor: readOnly ? 'rgba(145,158,171,0.2)' : 'rgba(145,158,171,0.4)' },
            '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
        },
        '& .MuiOutlinedInput-input': { py: '8px', px: '10px' },
    };

    return (
        <Box>
            {/* ── Cost items table ── */}
            <Box
                sx={{
                    borderRadius: '10px',
                    border: '1px solid rgba(145,158,171,0.16)',
                    overflow: 'hidden',
                    mb: 2,
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#F4F6F8' }}>
                            <TableCell sx={{ ...cellSx, pl: '14px', fontWeight: 700, fontSize: '11px', color: '#637381', textTransform: 'uppercase', letterSpacing: '0.5px', width: '42%' }}>
                                Item Description
                            </TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, fontSize: '11px', color: '#637381', textTransform: 'uppercase', letterSpacing: '0.5px', width: '12%' }}>
                                Qty
                            </TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, fontSize: '11px', color: '#637381', textTransform: 'uppercase', letterSpacing: '0.5px', width: '20%' }}>
                                Unit Price
                            </TableCell>
                            <TableCell sx={{ ...cellSx, fontWeight: 700, fontSize: '11px', color: '#637381', textTransform: 'uppercase', letterSpacing: '0.5px', width: '18%' }}>
                                Total
                            </TableCell>
                            {!readOnly && <TableCell sx={{ ...cellSx, width: '8%' }} />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {costItems.map((item) => {
                            const qty  = parseFloat(item.qty) || 0;
                            const unit = parseFloat(item.unitPrice) || 0;
                            const lineTotal = qty * unit;
                            return (
                                <TableRow
                                    key={item.id}
                                    sx={{
                                        '& td': { border: 'none', borderBottom: '1px solid rgba(145,158,171,0.08)' },
                                        '&:last-child td': { borderBottom: 'none' },
                                    }}
                                >
                                    <TableCell sx={{ ...cellSx, pl: '14px' }}>
                                        {readOnly ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="e.g. Implant fixture"
                                                value={item.description}
                                                disabled
                                                sx={inputSx}
                                            />
                                        ) : (
                                            <Autocomplete
                                                freeSolo
                                                size="small"
                                                options={treatmentOptions}
                                                getOptionLabel={(option) =>
                                                    typeof option === 'string' ? option : option.label || ''
                                                }
                                                groupBy={(option) => option.category || 'Other'}
                                                inputValue={item.description || ''}
                                                onInputChange={(_, newInput, reason) => {
                                                    if (reason === 'input' || reason === 'reset') {
                                                        updateItem(item.id, 'description', newInput);
                                                    }
                                                }}
                                                onChange={(_, newValue) => handleTreatmentSelect(item.id, newValue)}
                                                filterOptions={(opts, state) => {
                                                    const input = state.inputValue.toLowerCase();
                                                    if (!input) return opts;
                                                    return opts.filter((o) => o.label.toLowerCase().includes(input));
                                                }}
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option.label}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                            {option.colour && (
                                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: option.colour, flexShrink: 0 }} />
                                                            )}
                                                            <Typography sx={{ fontSize: '13px', flex: 1 }}>{option.label}</Typography>
                                                            {option.price && (
                                                                <Typography sx={{ fontSize: '12px', color: '#637381', flexShrink: 0 }}>
                                                                    £{parseFloat(option.price).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                )}
                                                noOptionsText={treatmentOptions.length === 0 ? 'Loading treatments...' : 'No matching treatments'}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Search treatments or type freely..."
                                                        sx={inputSx}
                                                    />
                                                )}
                                                componentsProps={{
                                                    paper: {
                                                        sx: {
                                                            borderRadius: '10px',
                                                            boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                                                            mt: 0.5,
                                                            '& .MuiAutocomplete-groupLabel': {
                                                                fontSize: '11px',
                                                                fontWeight: 700,
                                                                color: '#637381',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.5px',
                                                                bgcolor: '#F4F6F8',
                                                                lineHeight: '32px',
                                                            },
                                                            '& .MuiAutocomplete-option': {
                                                                fontSize: '13px',
                                                                py: '6px',
                                                                px: '12px',
                                                            },
                                                        },
                                                    },
                                                }}
                                                disableClearable
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.qty}
                                            onChange={(e) => updateItem(item.id, 'qty', e.target.value)}
                                            disabled={readOnly}
                                            inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                            sx={{ ...inputSx, '& input': { px: '6px' } }}
                                        />
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={item.unitPrice}
                                            onChange={(e) => updateItem(item.id, 'unitPrice', e.target.value)}
                                            disabled={readOnly}
                                            InputProps={{
                                                inputProps: { min: 0 },
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Typography sx={{ fontSize: '12px', color: '#637381' }}>£</Typography>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={inputSx}
                                        />
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: lineTotal > 0 ? '#212B36' : 'text.disabled',
                                                pl: '10px',
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {lineTotal > 0 ? `£${formatCurrency(lineTotal)}` : '—'}
                                        </Typography>
                                    </TableCell>
                                    {!readOnly && (
                                        <TableCell sx={{ ...cellSx, pr: '8px' }}>
                                            <Tooltip title="Remove row">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeItem(item.id)}
                                                    sx={{
                                                        color: '#C4CDD5',
                                                        '&:hover': { color: '#FF4842', bgcolor: alpha('#FF4842', 0.08) },
                                                    }}
                                                >
                                                    <Iconify icon="eva:trash-2-fill" width={15} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}

                        {/* Grand total row */}
                        <TableRow sx={{ bgcolor: '#F9FAFB', '& td': { border: 'none', borderTop: '2px solid rgba(145,158,171,0.16)' } }}>
                            <TableCell colSpan={readOnly ? 2 : 2} sx={{ ...cellSx, pl: '14px' }} />
                            <TableCell sx={{ ...cellSx }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '11px', color: '#637381', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Grand Total
                                </Typography>
                            </TableCell>
                            <TableCell sx={cellSx}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        color: '#00AB55',
                                        pl: '10px',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {grandTotal > 0 ? `£${formatCurrency(grandTotal)}` : '—'}
                                </Typography>
                            </TableCell>
                            {!readOnly && <TableCell sx={cellSx} />}
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>

            {/* Add row button */}
            {!readOnly && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Iconify icon="eva:plus-fill" width={14} />}
                    onClick={addItem}
                    sx={{
                        mb: 2.5,
                        fontSize: '12px',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        borderColor: 'rgba(145,158,171,0.32)',
                        color: '#637381',
                        '&:hover': { borderColor: '#00AB55', color: '#00AB55', bgcolor: alpha('#00AB55', 0.04) },
                    }}
                >
                    Add item
                </Button>
            )}

            {/* ── Deposit & payment plan ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Deposit required
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        placeholder="0.00"
                        value={deposit || ''}
                        onChange={(e) => onChange('deposit', e.target.value)}
                        disabled={readOnly}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Typography sx={{ fontSize: '12px', color: '#637381' }}>£</Typography>
                                </InputAdornment>
                            ),
                        }}
                        sx={inputSx}
                    />
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Payment plan
                    </Typography>
                    <Select
                        size="small"
                        fullWidth
                        value={paymentPlan?.type || 'no'}
                        onChange={(e) => onChange('paymentPlan', { ...(paymentPlan || {}), type: e.target.value, details: e.target.value === 'no' ? '' : (paymentPlan?.details || '') })}
                        disabled={readOnly}
                        sx={{
                            borderRadius: '8px',
                            fontSize: '13px',
                            bgcolor: readOnly ? '#F9FAFB' : '#fff',
                            '& fieldset': { borderColor: 'rgba(145,158,171,0.2)' },
                            '& .MuiSelect-select': { py: '8px', px: '10px' },
                        }}
                    >
                        {PAYMENT_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value} sx={{ fontSize: '13px' }}>{o.label}</MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>

            {/* Payment plan details (conditional) */}
            {showPaymentDetails && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Payment plan details
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g. 12 monthly payments of £200, interest free"
                        value={paymentPlan?.details || ''}
                        onChange={(e) => onChange('paymentPlan', { ...paymentPlan, details: e.target.value })}
                        disabled={readOnly}
                        sx={inputSx}
                    />
                </Box>
            )}

            {/* Included / excluded */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        What&apos;s included
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="List everything covered by the quoted price..."
                        value={included || ''}
                        onChange={(e) => onChange('included', e.target.value)}
                        disabled={readOnly}
                        sx={{
                            ...inputSx,
                            '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], alignItems: 'flex-start' },
                        }}
                    />
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        What&apos;s not included
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="List any exclusions or potential additional costs..."
                        value={excluded || ''}
                        onChange={(e) => onChange('excluded', e.target.value)}
                        disabled={readOnly}
                        sx={{
                            ...inputSx,
                            '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], alignItems: 'flex-start' },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

TreatmentPlanCostTable.propTypes = {
    costItems:   PropTypes.array,
    deposit:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentPlan: PropTypes.shape({ type: PropTypes.string, details: PropTypes.string }),
    included:    PropTypes.string,
    excluded:    PropTypes.string,
    onChange:    PropTypes.func.isRequired,
    readOnly:    PropTypes.bool,
    treatments:  PropTypes.array,
};

TreatmentPlanCostTable.defaultProps = {
    costItems:   [],
    deposit:     '',
    paymentPlan: { type: 'no', details: '' },
    included:    '',
    excluded:    '',
    readOnly:    false,
    treatments:  [],
};
